import { useMutation, useQueryClient } from "@tanstack/react-query"
import api from "@/lib/axios"
import FormHeader from "@/components/FormHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useState } from "react"
import { FaPlus } from "react-icons/fa6"
import { Loader2 } from "lucide-react"
import { Jalur } from "@/types"
import { MdModeEdit } from "react-icons/md"
import { RiDeleteBack2Line } from "react-icons/ri"
import toast from "react-hot-toast"
import { AxiosError } from "axios"

const postData = async (newData: { nama_jalur: string }) => {
  const response = await api.post("/master/jalur/store", newData)
  return response.data
}

const patchData = async (newData: {
  id_ref_jalur: string
  nama_jalur: string
}) => {
  const response = await api.patch("/master/jalur/update", newData)
  return response.data
}

const deleteData = async (deletedData: { id_ref_jalur: string }) => {
  const response = await api({
    url: "/master/jalur/delete",
    method: "DELETE",
    data: deletedData,
  })
  return response.data
}

export default function Form({
  method,
  selectedRow,
  closeModal,
}: {
  method: "POST" | "PATCH" | "DELETE"
  selectedRow?: Jalur | null
  closeModal: () => void
}) {
  const [form, setForm] = useState({
    id_ref_jalur: selectedRow?.id_ref_jalur || "",
    nama_jalur: selectedRow?.nama_jalur || "",
  })

  const isPost = method === "POST"
  const isPatch = method === "PATCH"
  const isDelete = method === "DELETE"

  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listJalur"] })
      closeModal()
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    mutate(form)
  }

  if (isDelete)
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <FormHeader
          icon={<RiDeleteBack2Line />}
          title="Delete Data Jalur"
          description={`Apakah anda yakin akan menghapus data jalur "${selectedRow?.nama_jalur}"?`}
        />

        <div className="flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            disabled={isPending}
            onClick={() => closeModal()}
          >
            Batal
          </Button>
          <Button
            variant={"destructive"}
            className="flex items-center gap-3"
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Hapus"
            )}
          </Button>
        </div>
      </form>
    )

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={isPost ? "Insert Data Jalur" : "Update Data jalur"}
      />
      <div>
        <Label>Nama Jalur</Label>
        <Input
          placeholder="Ketik nama jalur..."
          value={form.nama_jalur}
          onChange={(event) =>
            setForm((prev) => ({ ...prev, nama_jalur: event.target.value }))
          }
        />
      </div>

      <Button className="mt-3 flex items-center gap-3" disabled={isPending}>
        {isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            Processing...
          </>
        ) : (
          "Simpan"
        )}
      </Button>
    </form>
  )
}
